import { Typography, Stack, Box, LinearProgress } from "@mui/material";
import {
  FIRST_NAME,
  LAST_NAME,
  EMAIL,
  PHONE_NUMBER,
  TEST_PATIENTS,
  STATUS,
  LANGUAGE,
  STATE,
  ORDER_BY,
  ORDER,
  PATIENT_ID,
  REFILL,
} from "src/constants/searchParams";
import { useSearchParams } from "react-router-dom";
import { filters } from "./utils/filters";
import PatientsTable from "./components/PatientTable";
import { useMemo } from "react";
import Filter from "src/components/TableFilter";
import { reversedStatesMap } from "src/constants/usStates";
import { useGetPatientProfilesQuery } from "src/api/services/patientsApi";
import SearchById from "src/shared/SearchById";
import { reversedMonthsMap } from "src/constants/monthsMap";
import { useFromAndTo } from "src/hooks/useFromAndTo";

const Patients = () => {
  const [params] = useSearchParams();
  const { from, to } = useFromAndTo();

  const stateFilter = useMemo(() => {
    const state = params.get(STATE);
    if (state != null) {
      return reversedStatesMap[state];
    }

    return undefined;
  }, [params]);

  const monthFilter = useMemo(() => {
    const month = params.get(REFILL);

    if (month != null) {
      return Number(reversedMonthsMap[month]);
    }
  }, [params]);

  const queryParams = useMemo(() => {
    const patientId = params.get(PATIENT_ID);
    if (patientId != null) {
      return {
        id: patientId,
        from,
        to,
      };
    }
    return {
      order_by: params.get(ORDER_BY) ?? undefined,
      ascending: params.get(ORDER) === "asc" ? true : false,
      region: stateFilter,
      language: params.get(LANGUAGE) ?? undefined,
      status: params.get(STATUS) ?? undefined,
      first_name: params.get(FIRST_NAME) ?? undefined,
      last_name: params.get(LAST_NAME) ?? undefined,
      email: params.get(EMAIL) ?? undefined,
      phone_number: params.get(PHONE_NUMBER) ?? undefined,
      test_patients: params.get(TEST_PATIENTS) ?? undefined,
      refill_month: monthFilter,
      from,
      to,
    };
  }, [from, monthFilter, params, stateFilter, to]);

  const { data, isFetching } = useGetPatientProfilesQuery(queryParams);

  const { patients, count } = useMemo(() => {
    if (data == null) return { patients: [], count: 0 };

    return { patients: data.data, count: data.count };
  }, [data]);

  return (
    <Stack gap="32px">
      <Typography variant="h4" fontWeight={600}>
        Patients/Clients
      </Typography>
      <Stack
        direction="row"
        justifyContent="space-between"
        height={60}
        alignItems="center"
      >
        <Stack direction="row" gap="16px" flexWrap="wrap">
          {filters.map((filter) => (
            <Filter key={filter.name} current={filter} />
          ))}
          <SearchById name={PATIENT_ID} label="Patient ID" />
        </Stack>
      </Stack>
      <Stack gap={0}>
        <Box height={4}>
          {isFetching ? <LinearProgress color="success" /> : null}
        </Box>
        <PatientsTable patients={patients} total={count} />
      </Stack>
    </Stack>
  );
};

export default Patients;
